import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Wrench, CheckCircle2, TrendingUp, FileText, Mail, Star, ClipboardList, Download } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OverviewTab = ({ stats, setActiveSection, onOpenDocLeads }) => {
  const [q, setQ] = useState({ total: 0, pending: 0, approved: 0, declined: 0 });
  const [issues, setIssues] = useState([]);
  const [downloads, setDownloads] = useState({ total: 0, items: [] });

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/admin/download-stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setDownloads(res.data || { total: 0, items: [] }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const now = new Date();
        const counts = {};
        (res.data || []).forEach((l) => {
          if (!Array.isArray(l.symptoms) || !l.symptoms.length) return;
          const d = new Date(l.created_at);
          if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return;
          l.symptoms.forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
        });
        const arr = Object.entries(counts)
          .map(([label, count]) => ({ label, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6);
        setIssues(arr);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/admin/repair-quotes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const list = res.data || [];
        setQ({
          total: list.length,
          pending: list.filter((x) => x.status === 'pending').length,
          approved: list.filter((x) => x.status === 'approved').length,
          declined: list.filter((x) => x.status === 'declined').length,
        });
      })
      .catch(() => {});
  }, []);

  const decided = q.approved + q.declined;
  const winRate = decided > 0 ? Math.round((q.approved / decided) * 100) : null;
  const leadsTotal = stats?.leads_count || stats?.total_leads || 0;

  const cards = [
    { label: 'Total Leads', value: leadsTotal, icon: Users, color: 'bg-orange-100 text-orange-600' },
    { label: 'New Diagnostic Requests', value: q.pending, icon: Wrench, color: 'bg-amber-100 text-amber-600' },
    { label: 'Approved Rebuilds', value: q.approved, icon: CheckCircle2, color: 'bg-indigo-100 text-indigo-600' },
    { label: 'Win Rate', value: winRate === null ? '—' : `${winRate}%`, icon: TrendingUp, color: 'bg-green-100 text-green-600', sub: 'of decided quotes' },
  ];

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-[#003456] mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {cards.map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 shadow-sm" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>{stat.value}</p>
            <p className="text-gray-500 text-sm">{stat.label}</p>
            {stat.sub && <p className="text-xs text-gray-400 mt-1">{stat.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-[#003456] mb-4">Recent Leads</h3>
          <div className="space-y-4">
            {stats?.recent_leads?.slice(0, 5).map((lead, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="font-medium text-[#003456]">{lead.name}</p>
                  <p className="text-sm text-gray-500">{lead.email}</p>
                </div>
                <span className="px-3 py-1 rounded text-xs font-medium bg-[#003456]/10 text-[#003456]">
                  {lead.type || 'lead'}
                </span>
              </div>
            ))}
            {(!stats?.recent_leads || stats.recent_leads.length === 0) && (
              <p className="text-gray-500 text-center py-4">No leads yet</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-[#003456] mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed hover:border-[#CE8946]" onClick={() => setActiveSection('repair-quotes')} data-testid="quick-diagnostic-quotes">
              <Wrench className="h-6 w-6 text-[#CE8946]" />
              <span>Diagnostic Quotes</span>
            </Button>
            <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed hover:border-[#CE8946]" onClick={() => setActiveSection('leads')} data-testid="quick-view-leads">
              <Mail className="h-6 w-6 text-[#CE8946]" />
              <span>View Leads</span>
            </Button>
            <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed hover:border-[#CE8946]" onClick={() => setActiveSection('testimonials')} data-testid="quick-testimonials">
              <Star className="h-6 w-6 text-[#CE8946]" />
              <span>Testimonials</span>
            </Button>
            <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed hover:border-[#CE8946]" onClick={() => setActiveSection('documents')} data-testid="quick-documents">
              <FileText className="h-6 w-6 text-[#CE8946]" />
              <span>Documents</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Whitepaper & guide download counters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mt-6" data-testid="overview-download-counts">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-[#CE8946]" />
            <h3 className="text-lg font-semibold text-[#003456]">Whitepaper &amp; guide downloads</h3>
          </div>
          <span className="text-sm text-gray-500">{downloads.total} total</span>
        </div>
        {(!downloads.items || downloads.items.length === 0) ? (
          <p className="text-gray-500 text-sm py-4">No downloads yet. Each time someone unlocks a case study or guide with their email, it will be counted here.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {downloads.items.map((d) => (
              <button
                key={d.title}
                type="button"
                onClick={() => onOpenDocLeads && onOpenDocLeads(d.title)}
                className="w-full flex items-center justify-between py-3 text-left hover:bg-[#CE8946]/5 -mx-2 px-2 rounded-lg transition-colors group"
                data-testid={`download-count-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)}`}
                title="View everyone who downloaded this"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 text-[#CE8946] flex-shrink-0" />
                  <span className="text-sm text-[#003456] font-medium truncate group-hover:underline">{d.title}</span>
                </div>
                <span className="text-sm font-bold text-[#003456] bg-[#CE8946]/15 rounded-full px-3 py-0.5 ml-3 flex-shrink-0" style={{ fontFamily: 'JetBrains Mono' }}>{d.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Most common issues this month (from quote-form symptom tags) */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mt-6" data-testid="overview-common-issues">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="h-5 w-5 text-[#CE8946]" />
          <h3 className="text-lg font-semibold text-[#003456]">Most common issues this month</h3>
        </div>
        {issues.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No tagged issues reported yet this month. Symptoms selected on the machine quote forms will show up here.</p>
        ) : (
          <div className="space-y-3">
            {issues.map((it) => {
              const max = issues[0].count || 1;
              return (
                <div key={it.label} data-testid={`issue-bar-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#003456] font-medium">{it.label}</span>
                    <span className="text-gray-500 font-mono">{it.count}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-[#CE8946]" style={{ width: `${(it.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;
